import { useEffect, useMemo, useState } from 'react';
import BottomNav from '../components/BottomNav';
import OnlineBadge from '../components/OnlineBadge';
import ActivityRing from '../components/ActivityRing';
import { useSession } from '../auth/session';
import { operatorApi, type DashboardData } from '../api/operator';
import { statusUi, ago } from '../lib/statusUi';
import type { ReadingStatus } from '../lib/schedule';
import { fmtTime } from '../lib/format';
import { readCache, writeCache } from '../lib/swr';
import { groupBy } from '../lib/pmsGroup';

const CACHE_KEY = 'operatorDashboard';

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

/** Read-only equipment status overview for operators. */
export default function OperatorDashboard() {
  const session = useSession();
  const [data, setData] = useState<DashboardData | null>(() => readCache<DashboardData>(CACHE_KEY)?.data ?? null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(() => !readCache<DashboardData>(CACHE_KEY));
  const [view, setView] = useState<'daily' | 'pms'>('daily');

  const load = () => {
    if (!readCache<DashboardData>(CACHE_KEY)) setLoading(true);
    setErr('');
    operatorApi.dashboard()
      .then((d) => { setData(d); writeCache(CACHE_KEY, d); })
      .catch((e) => { if (!readCache<DashboardData>(CACHE_KEY)) setErr(e instanceof Error ? e.message : String(e)); })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const dailyEquip = useMemo(() => (data?.equipment ?? []).filter((e) => (e.category ?? 'daily') === 'daily'), [data]);
  const pmsEquip = useMemo(() => (data?.equipment ?? []).filter((e) => e.category === 'pms'), [data]);
  const pmsGroups = useMemo(() => groupBy(pmsEquip, (e) => e.pmsGroup || 'Other'), [pmsEquip]);
  const shownEquip = view === 'daily' ? dailyEquip : pmsEquip;

  const onSchedulePct = (list: DashboardData['equipment']) =>
    list.length ? Math.round(((list.length - list.filter((e) => e.status === 'OVERDUE').length) / list.length) * 100) : 100;
  const dailyPct = onSchedulePct(dailyEquip);
  const pmsPct = onSchedulePct(pmsEquip);

  return (
    <div className="app has-bottomnav">
      <OnlineBadge />

      <div className="dashHero">
        <div>
          <h1 className="dashHero-title">{greeting()}{session?.operatorName ? `, ${session.operatorName.split(' ')[0]}` : ''}</h1>
          <div className="dashHero-sub">
            {new Date().toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })}
          </div>
        </div>
        <button className="sm" onClick={load}>↻</button>
      </div>

      {err && <p className="error">{err}</p>}
      {loading && <div className="spinner" />}

      {data && (
        <>
          <div className="ringGrid">
            <div className="ringCard">
              <ActivityRing pct={dailyPct} tone={dailyPct === 100 ? 'ok' : dailyPct >= 70 ? 'warn' : 'bad'} size={50} stroke={6} />
              <div className="ringLabel">
                <div className="ringPct">{dailyPct}%</div>
                <div className="ringName">Daily on schedule</div>
              </div>
            </div>
            <div className="ringCard">
              <ActivityRing pct={pmsPct} tone={pmsPct === 100 ? 'ok' : pmsPct >= 70 ? 'warn' : 'bad'} size={50} stroke={6} />
              <div className="ringLabel">
                <div className="ringPct">{pmsPct}%</div>
                <div className="ringName">PMS on schedule</div>
              </div>
            </div>
          </div>

          <div className="statRow">
            <Stat icon="📈" n={data.totals.readingsToday} label="Readings today" />
            <Stat icon="⏱️" n={data.totals.due} label="Due now" tone={data.totals.due ? 'warn' : undefined} />
            <Stat icon="⚠️" n={data.totals.overdue} label="Overdue" tone={data.totals.overdue ? 'bad' : undefined} />
          </div>

          <h2 className="sectionHead">Equipment</h2>
          <div className="choice-group" role="tablist" aria-label="Equipment view" style={{ marginBottom: 12 }}>
            <button type="button" className={`choice-btn ${view === 'daily' ? 'on' : ''}`} role="tab" aria-selected={view === 'daily'} onClick={() => setView('daily')}>
              Daily readings ({dailyEquip.length})
            </button>
            <button type="button" className={`choice-btn ${view === 'pms' ? 'on' : ''}`} role="tab" aria-selected={view === 'pms'} onClick={() => setView('pms')}>
              PMS checks ({pmsEquip.length})
            </button>
          </div>

          {shownEquip.length === 0 && <p className="muted center tiny">Nothing here yet.</p>}

          {view === 'daily' && (
            <div className="cardlist">
              {dailyEquip.map((e) => <EquipRow key={e.equipmentId} e={e} />)}
            </div>
          )}
          {view === 'pms' && pmsGroups.map((g) => (
            <div key={g.label} style={{ marginBottom: 4 }}>
              <h3 className="pmsGroup-head">{g.label}</h3>
              <div className="cardlist">
                {g.items.map((e) => <EquipRow key={e.equipmentId} e={e} />)}
              </div>
            </div>
          ))}
        </>
      )}

      <BottomNav />
    </div>
  );
}

function EquipRow({ e }: { e: DashboardData['equipment'][number] }) {
  const ui = statusUi(e.status as ReadingStatus);
  return (
    <div className="item">
      <div className={`stripe ${ui.tone === 'muted' ? '' : ui.tone}`} />
      <div className="body">
        <div className="spread">
          <span className="title">{e.name}</span>
          <span className={`chip ${ui.tone === 'bad' ? 'bad' : ui.tone === 'warn' ? 'warn' : 'ok'}`}>{ui.label}</span>
        </div>
        <div className="meta">
          <span>{e.location}</span>
          <span>Last {ago(e.lastReadingAt)}</span>
          <span>Next {fmtTime(e.nextRecommendedAt)}</span>
        </div>
      </div>
    </div>
  );
}

function Stat({ icon, n, label, tone }: { icon: string; n: number; label: string; tone?: 'bad' | 'warn' }) {
  return (
    <div className={`stat ${tone ?? ''}`}>
      <div className="statIc">{icon}</div>
      <div className="statN">{n}</div>
      <div className="statL">{label}</div>
    </div>
  );
}
